import { supabase } from '@/lib/supabase';
import { ServiceResult, createError } from './types';

export interface Property {
  id: string;
  name: string;
  address: string | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  thumbnail_url: string | null;
}

export interface ChecklistItem {
  id: string;
  task: string;
  type: 'task' | 'video';
  display_order: number;
}

export interface RoomPhoto {
  id: string;
  url: string;
  caption: string | null;
  display_order: number;
}

export interface RoomWithItems {
  id: string;
  name: string;
  icon: string | null;
  display_order: number;
  checklist_items: ChecklistItem[];
  room_photos: RoomPhoto[];
}

export async function fetchAllProperties(): Promise<ServiceResult<Property[]>> {
  // Fetch properties with rooms and their first photo for thumbnail
  const { data, error } = await supabase
    .from('properties')
    .select(`
      id, name, address, beds, baths, sqft,
      rooms(
        room_photos(url)
      )
    `)
    .order('beds', { ascending: true, nullsFirst: false })
    .order('name');

  if (error) {
    // If query failed (likely missing columns), try basic query
    const fallbackResult = await supabase
      .from('properties')
      .select('id, name, address')
      .order('name');
    
    if (fallbackResult.error) {
      return {
        data: null,
        error: createError('Failed to load properties', fallbackResult.error.message, fallbackResult.error.code),
      };
    }

    // Add null values for missing columns
    const dataWithDefaults = fallbackResult.data.map((p: { id: string; name: string; address: string | null }) => ({
      ...p,
      beds: null,
      baths: null,
      sqft: null,
      thumbnail_url: null,
    }));

    return { data: dataWithDefaults, error: null };
  }

  // Extract first room photo as thumbnail
  const propertiesWithThumbnails = data.map((p) => {
    const rooms = p.rooms as { room_photos: { url: string }[] }[] | undefined;
    let thumbnail_url: string | null = null;
    
    if (rooms) {
      for (const room of rooms) {
        if (room.room_photos && room.room_photos.length > 0) {
          thumbnail_url = room.room_photos[0].url;
          break;
        }
      }
    }

    return {
      id: p.id,
      name: p.name,
      address: p.address,
      beds: p.beds,
      baths: p.baths,
      sqft: p.sqft,
      thumbnail_url,
    };
  });

  return { data: propertiesWithThumbnails, error: null };
}

export async function fetchPropertyDetails(
  propertyId: string
): Promise<ServiceResult<Property>> {
  const { data, error } = await supabase
    .from('properties')
    .select('id, name, address, beds, baths, sqft')
    .eq('id', propertyId)
    .single();

  if (error) {
    return {
      data: null,
      error: createError('Property not found', error.message, error.code),
    };
  }

  return { 
    data: { ...data, thumbnail_url: null }, 
    error: null 
  };
}

export async function fetchPropertyWithChecklist(
  propertyId: string
): Promise<ServiceResult<RoomWithItems[]>> {
  const { data, error } = await supabase
    .from('rooms')
    .select(`
      id, name, icon, display_order,
      checklist_items(id, task, type, display_order),
      room_photos(id, url, caption, display_order)
    `)
    .eq('property_id', propertyId)
    .order('display_order', { ascending: true });

  if (error) {
    return {
      data: null,
      error: createError('Failed to load checklist', error.message, error.code),
    };
  }

  // Sort nested arrays by display_order
  const sortedData = data.map((room) => ({
    ...room,
    checklist_items: [...room.checklist_items].sort(
      (a, b) => a.display_order - b.display_order
    ),
    room_photos: [...room.room_photos].sort(
      (a, b) => a.display_order - b.display_order
    ),
  }));

  return { data: sortedData, error: null };
}


